import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Network, Pencil, Plus, Printer, Wand2 } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { KpiOrganigrama } from "@/componentes/kpi/KpiPlanificacion"
import { CompletarDesdeCargos } from "@/componentes/organigrama/CompletarDesdeCargos"
import { DialogoMapa } from "@/componentes/organigrama/DialogoMapa"
import { LienzoOrganigrama } from "@/componentes/organigrama/LienzoOrganigrama"
import { PanelPosicion } from "@/componentes/organigrama/PanelPosicion"
import type { Organigrama as Mapa, Nodo } from "@/componentes/organigrama/tipos"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/**
 * El organigrama: quién ocupa qué puesto y quién manda a quién.
 *
 * ⚠ **Una caja es una POSICIÓN, no una persona.** Referencia un cargo del catálogo y
 * —si está cubierta— una persona del expediente. De ahí salen gratis la vacante (una
 * posición sin persona) y el multi-cargo (dos posiciones con la misma). En
 * `organizacional`, de donde viene esto, el nodo guarda su propia copia del nombre y
 * del teléfono, y por eso allí un cambio hay que hacerlo en dos sitios.
 *
 * ⚠ **Puede haber varias cabezas.** Dos socios directores al mismo nivel es una
 * estructura real; obligar a subordinar uno al otro sería documentar una jerarquía que
 * no existe.
 *
 * ⚠ **Se puede arrastrar una tarjeta sobre otra para cambiarle el jefe**, y el panel
 * trae el mismo cambio como selector (WCAG 2.5.7). El servidor rechaza los ciclos: no
 * se puede colgar a alguien de su propio equipo.
 */

export function Organigrama() {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeEditar = tienePermiso(usuario, "organigrama:editar")

  const [elegido, setElegido] = useState<number | null>(null)
  const [posicion, setPosicion] = useState<number | null>(null)
  // `vez` remonta el diálogo en cada apertura: ver `DialogoMapa`.
  const [dialogoMapa, setDialogoMapa] = useState<{
    abierto: boolean
    mapa: Mapa | null
    vez: number
  }>({ abierto: false, mapa: null, vez: 0 })
  const [completando, setCompletando] = useState(false)
  const [creandoNodo, setCreandoNodo] = useState(false)
  const [cargoNuevo, setCargoNuevo] = useState("")

  const mapas = useQuery({
    queryKey: ["organigramas"],
    queryFn: () => api.get<Mapa[]>("/api/v1/organigramas"),
  })

  const mapa = mapas.data?.find((m) => m.id === elegido) ?? mapas.data?.[0] ?? null

  const nodos = useQuery({
    queryKey: ["organigrama", "nodos", mapa?.id],
    queryFn: () => api.get<Nodo[]>(`/api/v1/organigramas/${mapa?.id}/nodos`),
    enabled: mapa !== null,
  })

  const cargos = useQuery({
    queryKey: ["cargos", "activos"],
    queryFn: () => api.get<{ id: number; nombre: string }[]>("/api/v1/cargos?solo_activos=true"),
    enabled: creandoNodo,
  })

  const abrirMapa = (m: Mapa | null) =>
    setDialogoMapa((d) => ({ abierto: true, mapa: m, vez: d.vez + 1 }))

  const crearNodo = useMutation({
    mutationFn: () =>
      api.post<Nodo>(`/api/v1/organigramas/${mapa?.id}/nodos`, {
        cargo_id: Number(cargoNuevo),
        padre_id: null,
      }),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["organigrama", "nodos", mapa?.id] })
      setCreandoNodo(false)
      setCargoNuevo("")
    },
  })

  const reparentar = useMutation({
    mutationFn: ({ arrastrado, destino }: { arrastrado: number; destino: number }) =>
      api.patch<Nodo>(`/api/v1/nodos/${arrastrado}`, { padre_id: destino }),
    onSuccess: () =>
      clienteQuery.invalidateQueries({ queryKey: ["organigrama", "nodos", mapa?.id] }),
  })

  const abierta = nodos.data?.find((n) => n.id === posicion) ?? null

  return (
    <div>
      {/* Ni título ni frase propios: los pone la cabecera de «Planificación
          estratégica». Aquí arriba sólo van las acciones, a la derecha. */}
      <div className="no-print mb-4 flex flex-wrap justify-end gap-2">
        <Boton variante="secundario" onClick={() => window.print()}>
          <Printer size={16} aria-hidden="true" />
          Exportar PDF
        </Boton>
        {puedeEditar && (
          <Boton variante="primario" onClick={() => abrirMapa(null)}>
            <Plus size={16} aria-hidden="true" />
            Nuevo organigrama
          </Boton>
        )}
      </div>
      <KpiOrganigrama mapaId={mapa?.id} />

      {mapas.data && mapas.data.length > 1 && (
        <div className="no-print mb-4 flex flex-wrap gap-2">
          {mapas.data.map((m) => (
            <Boton
              key={m.id}
              variante={mapa?.id === m.id ? "primario" : "fantasma"}
              tamano="sm"
              onClick={() => {
                setElegido(m.id)
                setPosicion(null)
              }}
            >
              {m.nombre}
            </Boton>
          ))}
        </div>
      )}

      {(mapas.isError || nodos.isError) && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudo cargar el organigrama
        </p>
      )}

      {mapas.isPending && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando…
        </p>
      )}

      {mapas.data && mapas.data.length === 0 && (
        <div
          className="flex flex-col items-center gap-2 superficie py-12"
          style={{ color: "var(--fg-muted)" }}
        >
          <Network size={22} aria-hidden="true" />
          <p className="text-sm">
            {puedeEditar
              ? "Crea el primer organigrama para empezar"
              : "Todavía no hay ningún organigrama"}
          </p>
        </div>
      )}

      {mapa && (
        <section className="superficie p-4">
          <div className="no-print mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold">{mapa.nombre}</h2>
            {puedeEditar && (
              <div className="flex flex-wrap gap-2">
                <Boton
                  variante="fantasma"
                  tamano="sm"
                  aria-label={`Editar «${mapa.nombre}»`}
                  onClick={() => abrirMapa(mapa)}
                >
                  <Pencil size={14} aria-hidden="true" />
                  Editar
                </Boton>
                <Boton variante="fantasma" tamano="sm" onClick={() => setCompletando(true)}>
                  <Wand2 size={14} aria-hidden="true" />
                  Completar desde cargos
                </Boton>
                <Boton variante="fantasma" tamano="sm" onClick={() => setCreandoNodo(true)}>
                  <Plus size={14} aria-hidden="true" />
                  Añadir posición
                </Boton>
              </div>
            )}
          </div>

          {reparentar.isError && (
            <p role="alert" className="mb-3 text-sm" style={{ color: "var(--destructive)" }}>
              {reparentar.error instanceof ErrorApi
                ? reparentar.error.message
                : "No se pudo mover la posición"}
            </p>
          )}

          {puedeEditar && (nodos.data?.length ?? 0) > 1 && (
            <p className="no-print mb-3 text-xs" style={{ color: "var(--fg-muted)" }}>
              Arrastra una tarjeta sobre otra para cambiarle el jefe, o usa «Depende de» en el panel
              de la posición.
            </p>
          )}

          <LienzoOrganigrama
            nodos={nodos.data ?? []}
            seleccionada={posicion}
            arrastrable={puedeEditar}
            alElegir={setPosicion}
            alSoltar={
              puedeEditar
                ? (arrastrado, destino) => reparentar.mutate({ arrastrado, destino })
                : undefined
            }
          />
        </section>
      )}

      <DialogoMapa
        key={dialogoMapa.vez}
        abierto={dialogoMapa.abierto}
        mapa={dialogoMapa.mapa}
        onCerrar={() => setDialogoMapa((d) => ({ ...d, abierto: false }))}
        onGuardado={(guardado) => {
          // Archivado, deja de listarse: se vuelve al primero.
          setElegido(guardado.activo ? guardado.id : null)
          setDialogoMapa((d) => ({ ...d, abierto: false }))
        }}
      />

      {mapa && (
        <CompletarDesdeCargos
          abierto={completando}
          organigramaId={mapa.id}
          onCerrar={() => setCompletando(false)}
        />
      )}

      <Modal
        abierto={creandoNodo}
        titulo="Añadir una posición"
        descripcion="Nace como cabeza y sin ocupante. Desde su panel se le pone jefe y persona."
        onCerrar={() => setCreandoNodo(false)}
        ancho="sm"
      >
        <label htmlFor="cargo-nuevo" className="mb-1 block text-xs font-medium uppercase">
          Cargo
        </label>
        <select
          id="cargo-nuevo"
          value={cargoNuevo}
          onChange={(e) => setCargoNuevo(e.target.value)}
          className="mb-4 h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--surface)",
            color: "var(--fg)",
          }}
        >
          <option value="">Elegir…</option>
          {cargos.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nombre}
            </option>
          ))}
        </select>
        {crearNodo.isError && (
          <p role="alert" className="mb-3 text-sm" style={{ color: "var(--destructive)" }}>
            {crearNodo.error instanceof ErrorApi ? crearNodo.error.message : "No se pudo añadir"}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Boton variante="fantasma" onClick={() => setCreandoNodo(false)}>
            Cancelar
          </Boton>
          <Boton
            variante="primario"
            onClick={() => crearNodo.mutate()}
            disabled={!cargoNuevo || crearNodo.isPending}
          >
            Añadir
          </Boton>
        </div>
      </Modal>

      {abierta && (
        <PanelPosicion
          nodo={abierta}
          nodos={nodos.data ?? []}
          puedeEditar={puedeEditar}
          onCerrar={() => setPosicion(null)}
        />
      )}
    </div>
  )
}
